import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { AltArrowDown, CheckRead, Widget } from "@solar-icons/react"
import { useAuthStore } from "@/stores/auth"
import { useGetUserQuery } from "@/queries/user"
import { useUpdateUserMutation } from "@/queries/auth"
import { useNavigate } from "react-router-dom"
import {
  effectiveConsoleScope,
  getEligibleConsoleScopes,
  mergeTenantBusinessSummaries,
} from "@/helpers/console-scope"
import { ADMIN_HOME } from "@/constants/paths/admin"
import {
  CONSOLE_SCOPE_PLATFORM,
  CONSOLE_SCOPE_TENANT,
} from "@/constants/console-scope"
import type { AuthSession } from "@/types/auth"
import Avatar from "@/components/core/avatar"
import { cn } from "@/lib/utils"

type BusinessRow = { _id?: string; name?: string; image?: string }

export function ConsoleScopeSwitcher() {
  const { t } = useTranslation("common")
  const token = useAuthStore((state) => state.token)
  const consoleScope = useAuthStore((state) => state.consoleScope)
  const setConsoleScope = useAuthStore((state) => state.setConsoleScope)
  const setActiveBusiness = useAuthStore((state) => state.setActiveBusiness)
  const activeBusinessId = useAuthStore((state) => state.activeBusinessId)
  const storeUser = useAuthStore((state) => state.user)
  const { data } = useGetUserQuery({}, { skip: !token })
  const profile = (data ?? storeUser) as AuthSession | undefined
  const eligible = getEligibleConsoleScopes(profile ?? null)

  const businesses = useMemo(
    () => mergeTenantBusinessSummaries(profile ?? null) as BusinessRow[],
    [profile]
  )
  const { mutateAsync, isPending } = useUpdateUserMutation()
  const navigate = useNavigate()

  if (
    !eligible.includes(CONSOLE_SCOPE_PLATFORM) ||
    !eligible.includes(CONSOLE_SCOPE_TENANT)
  ) {
    return null
  }

  const effective = effectiveConsoleScope(consoleScope, profile ?? null)

  const activeBusiness =
    businesses.find((b) => b._id === activeBusinessId) ?? businesses[0]

  const goToPlatform = async () => {
    if (isPending || effective === CONSOLE_SCOPE_PLATFORM) return
    await mutateAsync({ consoleLandingScope: CONSOLE_SCOPE_PLATFORM })
    setConsoleScope(CONSOLE_SCOPE_PLATFORM)
    navigate(ADMIN_HOME)
  }

  const selectBusiness = async (businessId: string) => {
    if (isPending) return
    if (
      effective === CONSOLE_SCOPE_TENANT &&
      activeBusinessId === businessId
    ) {
      return
    }

    if (effective === CONSOLE_SCOPE_PLATFORM) {
      await mutateAsync({
        consoleLandingScope: CONSOLE_SCOPE_TENANT,
        lastActiveBusinessId: businessId,
      })
    } else {
      await mutateAsync({ lastActiveBusinessId: businessId })
    }
    setActiveBusiness(businessId)
    setConsoleScope(CONSOLE_SCOPE_TENANT)
    navigate("/dashboard")
  }

  const triggerLabel =
    effective === CONSOLE_SCOPE_PLATFORM
      ? t("consoleScope.adminConsole")
      : (activeBusiness?.name ?? t("consoleScope.business"))

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 max-w-[10rem] gap-1.5 px-2 text-xs sm:max-w-[14rem]"
          disabled={isPending}
          aria-label={t("consoleScope.switchAriaLabel")}
          data-tour-step-id="console-scope-switcher"
        >
          <span className="truncate font-medium">{triggerLabel}</span>
          <AltArrowDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72 p-1 sm:w-80">
        <DropdownMenuLabel className="px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {t("consoleScope.platform")}
        </DropdownMenuLabel>
        <DropdownMenuGroup>
          <DropdownMenuItem
            disabled={isPending}
            className="cursor-pointer gap-2 rounded-lg px-2 py-2"
            onSelect={() => {
              void goToPlatform()
            }}
          >
            <div className="flex size-8 shrink-0 items-center justify-center rounded-md border bg-muted/50">
              <Widget className="size-4 text-muted-foreground" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium leading-none">{t("consoleScope.adminConsole")}</p>
              <p className="mt-1 truncate text-xs text-muted-foreground">
                {t("consoleScope.adminDescription")}
              </p>
            </div>
            {effective === CONSOLE_SCOPE_PLATFORM ? (
              <CheckRead className="size-4 shrink-0 text-primary" aria-hidden />
            ) : (
              <span className="size-4 shrink-0" aria-hidden />
            )}
          </DropdownMenuItem>
        </DropdownMenuGroup>

        <DropdownMenuSeparator className="my-1" />

        <DropdownMenuLabel className="px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {t("consoleScope.yourBusinesses")}
        </DropdownMenuLabel>
        <DropdownMenuGroup className="max-h-64 overflow-y-auto overflow-x-hidden pr-0.5">
          {businesses.length === 0 ? (
            <div className="px-2 py-3 text-center text-xs text-muted-foreground">
              {t("consoleScope.noBusinesses")}
            </div>
          ) : (
            businesses.map((business, index) => {
              const id = business._id ?? `idx-${index}`
              const isActive =
                effective === CONSOLE_SCOPE_TENANT &&
                business._id === activeBusinessId
              return (
                <DropdownMenuItem
                  key={id}
                  disabled={isPending || !business._id}
                  className={cn(
                    "cursor-pointer gap-2 rounded-lg px-2 py-2",
                    isActive && "bg-accent/60"
                  )}
                  onSelect={() => {
                    if (business._id) void selectBusiness(business._id)
                  }}
                >
                  <Avatar
                    src={business.image?.trim() ? business.image : undefined}
                    alt={business.name ?? t("consoleScope.business")}
                    size="xs"
                    disabled
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium leading-none">
                      {business.name ?? t("consoleScope.business")}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {t("consoleScope.openDashboard")}
                    </p>
                  </div>
                  {isActive ? (
                    <CheckRead className="size-4 shrink-0 text-primary" aria-hidden />
                  ) : (
                    <span className="size-4 shrink-0" aria-hidden />
                  )}
                </DropdownMenuItem>
              )
            })
          )}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
